import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Sliders } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCart } from '@/context/CartContext';
import WishlistButton from '@/components/WishlistButton';
import { useSettings } from '@/services/settingsService';
import { useAuth } from '@/services/authService';
import { resolveEffectivePrice } from '@/utils/pricing';
import { thumb } from '@/lib/img';
import ProductQuickView from '@/components/ProductQuickView';

export default function ProductCard({ product }) {
  const { addItem, subtotal } = useCart();
  const navigate = useNavigate();
  const { settings } = useSettings();
  const { user } = useAuth();
  const hasVariants = (product.variants?.colors?.length || 0) > 0 || (product.variants?.sizes?.length || 0) > 0;
  // Role-aware pricing: pick public / store / wholesale / special based on viewer.
  const { price: effPrice, tier, tierLabel } = resolveEffectivePrice(product, user, subtotal || 0);
  // Precio Divisas: descuento configurable, aplicado sobre el precio ya resuelto.
  const discountPct = Math.max(0, parseFloat(settings?.usd_discount_percent) || 0);
  const divisasPrice = discountPct > 0 ? effPrice * (1 - discountPct / 100) : null;
  const [quickOpen, setQuickOpen] = useState(false);
  const openQuick = (e) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
    e.preventDefault();
    setQuickOpen(true);
  };
  return (
    <>
    <ProductQuickView product={product} open={quickOpen} onOpenChange={setQuickOpen} />
    <div className="group bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow duration-300 flex flex-col">
      <Link to={`/producto/${product.id}`} onClick={openQuick} data-testid={`pc-quickview-img-${product.id}`} className="block relative aspect-square bg-slate-50 overflow-hidden">
        <img
          src={thumb(product.image, 400)}
          alt={`${product.name} al mayor · BIGTIME Venezuela${product.category ? ' · ' + product.category : ''}`}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        {product.sold_out && (
          <span className="absolute bottom-2 left-2 bg-red-600 text-white text-[11px] font-bold px-2 py-1 rounded-full uppercase" data-testid={`pc-sold-out-${product.id}`}>Agotado</span>
        )}
        {product.isNew && (
          <span className="absolute top-2 left-2 bg-[#1B2A4A] text-yellow-300 text-[11px] font-bold px-2 py-1 rounded-full uppercase tracking-wide">Nuevo</span>
        )}
        <div className="absolute top-2 right-2" onClick={(e) => e.stopPropagation()}>
          <WishlistButton productId={product.id} />
        </div>
      </Link>
      <div className="p-3 flex flex-col flex-1">
        <Link to={`/producto/${product.id}`} onClick={openQuick} data-testid={`pc-quickview-name-${product.id}`} className="text-sm text-slate-800 line-clamp-2 min-h-[40px] hover:text-[#1B2A4A]">
          {product.name}
        </Link>
        <div className="mt-2 flex items-end justify-between">
          <div>
            <div className="text-[11px] text-slate-400 -mb-0.5 flex items-center gap-1">
              <span>Precio</span>
              {tier !== 'public' && (
                <span
                  className="bg-[#1B2A4A] text-yellow-300 rounded px-1.5 py-[1px] text-[9px] font-bold uppercase tracking-wide"
                  data-testid={`pc-tier-${product.id}`}
                >
                  {tierLabel}
                </span>
              )}
            </div>
            <div className="text-lg font-extrabold text-[#1B2A4A]" data-testid={`pc-price-${product.id}`}>${effPrice.toFixed(2)}</div>
            {divisasPrice != null && (
              <div className="text-[11px] mt-0.5 flex items-center gap-1" data-testid={`pc-price-divisas-${product.id}`}>
                <span className="bg-yellow-100 text-[#1B2A4A] font-bold rounded px-1.5 py-0.5">Divisas</span>
                <span className="font-bold text-green-600">${divisasPrice.toFixed(2)}</span>
                <span className="text-slate-400">−{discountPct}%</span>
              </div>
            )}
          </div>
          <Button
            size="sm"
            disabled={!!product.sold_out}
            onClick={(e) => {
              e.preventDefault();
              if (hasVariants) navigate(`/producto/${product.id}`);
              else addItem(product);
            }}
            data-testid={hasVariants ? `pc-choose-${product.id}` : `pc-add-${product.id}`}
            aria-label={hasVariants ? 'Elegir opciones' : 'Agregar al carrito'}
            className="bg-[#F8D13F] hover:bg-yellow-300 text-[#1B2A4A] font-semibold rounded-full h-10 w-10 p-0 sm:w-auto sm:h-9 sm:px-3 flex-shrink-0"
          >
            {hasVariants ? <><Sliders size={16} className="sm:mr-1" /><span className="hidden sm:inline">Elegir</span></> : <><Plus size={18} className="sm:mr-1" /><span className="hidden sm:inline">Agregar</span></>}
          </Button>
        </div>
      </div>
    </div>
    </>
  );
}
